import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Permission } from '@core/enums/permissions';
import { SelectType } from '@core/enums/select';
import { IAgeGradeInstrument } from '@core/interfaces/iage-grade-instrument';
import { IClassroomAssignment } from '@core/interfaces/iclasses';
import { IPortfolioClassesRequest } from '@core/interfaces/iportfolio';
import { IRatingPeriod } from '@core/interfaces/iratingperiod';
import { AgencyService } from '@core/services/agency.service';
import { AuthService } from '@core/services/auth.service';
import { ClassService } from '@core/services/class.service';
import { setIconDisplayObject } from '@core/services/helper.service';
import { LookupService } from '@core/services/lookup.service';
import { PermissionService } from '@core/services/permission.service';
import { PortfolioService } from '@core/services/portfolio.service';
import { RatingPeriodService } from '@core/services/rating-period.service';
import { ToastService } from '@core/services/toast.service';
import { FilterSelectComponent } from '@shared/components/dropdowns/filter-select/filter-select.component';
import { take } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'drdp-portfolio',
    templateUrl: './portfolio.component.html',
    styleUrls: ['./portfolio.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PortfolioComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('classroomSelect') classroomSelect:
    | FilterSelectComponent
    | undefined;
  @ViewChild('agencySelect') agencySelect: FilterSelectComponent | undefined;
  @ViewChild('sitesSelect') sitesSelect: FilterSelectComponent | undefined;
  @ViewChild('ratingPeriodSelect') ratingPeriodSelect:
    | FilterSelectComponent
    | undefined;
  @ViewChild('stateSelect') stateSelect: FilterSelectComponent | undefined;
  @ViewChild('ageGradeInstrumentSelect') ageGradeInstrumentSelect:
    | FilterSelectComponent
    | undefined;

  dataSource: MatTableDataSource<any> = new MatTableDataSource();

  pageSizeOptions = [5, 10, 25];
  pageSize = 10;
  totalData = 0;
  tableData?: any;
  pageIndex: number = 0;

  tableColumns = [
    { columnDef: 'siteName', header: this.translocoService.translate('user.dashboard.portfolio.table.site'), type: 'text' },
    { columnDef: 'ratingPeriodName', header: this.translocoService.translate('user.dashboard.portfolio.table.ratingPeriod'), type: 'text' },
    { columnDef: 'classroomName', header: this.translocoService.translate('user.dashboard.portfolio.filters.classroom'), type: 'text' },
    { columnDef: 'View Students', header: this.translocoService.translate('user.dashboard.portfolio.table.viewStudents'), type: 'view' },
    { columnDef: 'classEvidence', header: this.translocoService.translate('user.dashboard.portfolio.table.evidenceByClass'), type: 'icon', icon: 'abacus', colorClass: 'text-drdpblue-300', cursorClass: 'cursor-pointer' },
  ];

  user = this.auth.getCurrentUser();
  selectForm: FormGroup | any;
  initStateId: number | null = this.user.stateId;
  initAgencyId: number | null = this.user.agencyId;
  classroomList: IClassroomAssignment[] = [];
  ageGroupInstrumentList: IAgeGradeInstrument[] = [];
  ratingPeriodList: IRatingPeriod[] = [];
  select = SelectType;
  clearState = false;
  clearAgency = false;
  clearSite = false;
  clearRatingPeriod = false;
  cleardropDown = false;
  currentSchoolYear = 0;
  agencySoftImplementationEnabled: boolean = false;
  siteSoftImplementationEnabled: boolean = false;
  classroomSoftImplementationEnabled: boolean = false;
  fieldStudyEnabledRatingPeriod: boolean = false;
  selectedSchoolYearId: number | null = null;
  isSuperUser: boolean = this.permissionService.checkPermission(
    Permission.AccessAllAgencies
  );
  searchValues = {
    ratingPeriodName: '',
    classroomName: '',
    ageGradeInstrumentId: 0,
  };

  get stateId() {
    return this.selectForm.get('stateId');
  }

  get agencyId() {
    return this.selectForm.get('agencyId');
  }
  get siteId() {
    return this.selectForm.get('siteId');
  }
  get ratingPeriodId() {
    return this.selectForm.get('ratingPeriodId');
  }
  get classroomId() {
    return this.selectForm.get('classroomId');
  }
  get ageGroupInstrumentId() {
    return this.selectForm.get('ageGroupInstrumentId');
  }

  get include2025AgeGrades() {
    return (this.agencySoftImplementationEnabled || this.siteSoftImplementationEnabled || this.classroomSoftImplementationEnabled)
      || this.fieldStudyEnabledRatingPeriod;
  }

  constructor(
    private auth: AuthService,
    private fb: FormBuilder,
    private ratingPeriodService: RatingPeriodService,
    private classService: ClassService,
    private lookup: LookupService,
    private portfolioService: PortfolioService,
    private router: Router,
    private toast: ToastService,
    private permissionService: PermissionService,
    private schoolyearService: LookupService,
    private agencyService: AgencyService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    this.dataSource.paginator = this.paginator;
    this.initializeForm();
    this.schoolyearService
      .getCurrentSchoolYear()
      .pipe(take(1))
      .subscribe((year) => {
        this.currentSchoolYear = year.id;
      });
    
    if (this.user.agencyId) {
      this.setAgencySoftImplementationFlag(this.user.agencyId);
    }  
  }

  initializeForm(): void {
    this.selectForm = this.fb.group({
      stateId: [this.user.stateId ?? null, [Validators.required]],
      agencyId: [this.user.agencyId ?? null, [Validators.required]],
      ratingPeriodId: [null, [Validators.required]],
      siteId: [null, [Validators.required]],
      classroomId: [null],
      ageGroupInstrumentId: [null, [Validators.required]],
    });

  }

  paginateTable(event: any) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.searchClasses();
  }

  viewStudents(event: any) {
    if (event && event.data)
      this.router.navigate([
        `/portfolio/class/${event.data.classId}/ageGradeInstrument/${this.ageGroupInstrumentId.value}`,
      ]);
  }

  viewClass(event: any): void {
    if (event && event.data) {
      this.searchValues.classroomName = event.data.classroomName;
      this.router.navigate(
        [`/portfolio/class-evidence/${event.data.classId}`],
        { state: { searchValues: this.searchValues }}
      );
    }
  }

  updateClassroomDropdown(): void {
    this.classroomList = [];
    const payload: any = {};
    payload.siteIds = this.siteId.value ?? null;
    payload.ratingPeriodId = this.ratingPeriodId.value ?? '';
    payload.itemsPerPage = 10;
    payload.useClassId = false;

    if (this.siteId.value?.length > 0 && payload.ratingPeriodId > 0) {
      this.classService
        .getClassesBySitesAndRatingPeriod(payload)
        .pipe(take(1))
        .subscribe((res: any) => {
          this.classroomList = res.filter(
            (value: any, index: any, self: any) =>
              index === self.findIndex((t: any) => t.id === value.id)
          );
        });
    } else {
      this.classroomList = [];
    }
  }

  getRatingPeriod(agencyId: number): void {
    this.ratingPeriodService
      .getByAgency(agencyId)
      .pipe(take(1))
      .subscribe((req) => {
        this.ratingPeriodList = req;
      });
  }

  private setAgencySoftImplementationFlag(agencyId: number): void {
    this.agencyService.getAgency(agencyId)
      .pipe(take(1))
      .subscribe((agency) => {
      this.agencySoftImplementationEnabled = agency?.isSoftImplementation ?? false;
    });
  }

  handleState(state: any) {
    this.stateId.setValue(state?.id);
    this.agencyId.setValue(null);
    this.ageGroupInstrumentId.setValue(null);
  }

  handleChosenAgency(agency: any): void {
    this.agencyId.setValue(agency?.id);
    this.agencySoftImplementationEnabled = agency?.isSoftImplementation ?? agency?.item?.isSoftImplementation ?? false;
    this.siteSoftImplementationEnabled = false;
    this.classroomSoftImplementationEnabled = false;
    this.classroomId.setValue(null);
    this.classroomList = [];
    if (agency) this.getRatingPeriod(agency.id);
    else this.ratingPeriodList = [];
  }

  handleChosenSite(site: any): void {
    if(site) {
      this.siteId.setValue([site.id]);
      this.siteSoftImplementationEnabled = site.item.isSoftImplementation;
    } else {
      this.siteId.setValue(null);
      this.siteSoftImplementationEnabled = false;
    }

    this.classroomSoftImplementationEnabled = false;
    this.updateClassroomDropdown();
  }

  handleChosenClassroom(classroom: any): void {
    this.classroomId.setValue(classroom?.id);
    this.classroomSoftImplementationEnabled = classroom?.item.isSoftImplementation ?? false;
  }

  handleChosenRatingPeriod(ratingPeriod: any): void {
    this.ratingPeriodId.setValue(ratingPeriod?.id);
    this.updateClassroomDropdown();
    this.searchValues.ratingPeriodName = ratingPeriod?.ratingPeriodName;
    this.fieldStudyEnabledRatingPeriod = ratingPeriod?.globalRatingPeriod?.isPilotEnabled ?? false;
    this.selectedSchoolYearId = ratingPeriod?.schoolYearId ?? null;
  }

  handleAgeGroupInstrument(ageGroupInstrument: any): void {
    this.ageGroupInstrumentId.setValue(ageGroupInstrument?.id);
    this.searchValues.ageGradeInstrumentId = ageGroupInstrument?.id;
  }

  clear() {
    this.classroomSelect?.clearSelected();
    this.ageGradeInstrumentSelect?.clearSelected();
    this.sitesSelect?.clearSelected();
    this.cleardropDown = true;
    this.classroomList = [];
    this.ratingPeriodId.setValue(null);
    this.selectedSchoolYearId = null;
    this.clearTable();

    if (!this.stateSelect?.disabled) {
      this.initStateId = 0;
      this.stateId.setValue(null);
      this.clearState = true;
    }

    if (!this.agencySelect?.disabled) {
      this.initAgencyId = null;
      this.agencyId.setValue(null);
      this.clearAgency = true;
      this.ratingPeriodList = [];
    }

  }

  initializeTableData(data: any): void {
    data.items = data.items.map((item: any) => ({
      ...item,
      displayIcons: setIconDisplayObject(this.tableColumns, item)
    }));
    this.dataSource = new MatTableDataSource(data.items);
    this.tableData = data.items;
    this.totalData = data.totalData;
  }

  clearTable(): void {
    const empty: any = [];
    this.dataSource = new MatTableDataSource(empty);
    this.totalData = 0;
    this.tableData = [];
  }

  submit() {
    if (!this.selectForm.valid) {
      this.toast.error(this.translocoService.translate('user.dashboard.portfolio.toast.requiredFields'));
      return;
    }
    this.searchClasses();
  }

  searchClasses() {
    const payload: IPortfolioClassesRequest = {
      stateId: this.stateId.value,
      ageGradeInstrumentId: this.ageGroupInstrumentId.value,
      classRoomId: this.classroomId.value,
      ratingPeriodId: this.ratingPeriodId.value,
      siteId: this.siteId.value[0] || null,
      pageIndex: this.pageIndex,
      itemsPerPage: this.pageSize,
    };
    this.portfolioService
      .searchClasses(payload)
      .pipe(take(1))
      .subscribe((res) => {
        if (res) {
          this.initializeTableData(res);
        }
      });
  }
}
